/**
 * The work order, drawn as a PDF.
 *
 * Lysa, 2026-09-12: "When scheduling WOs and MOs I can't tell if anything was
 * sent to the crews or distributor… Also if we can be sure the MO and WO both
 * reflect the homeowners name/address on the PDF file."
 *
 * A material order already went to the supplier as a file (ADR 0111). A work
 * order had no file at all — only the browser's print dialog, which produces
 * something to hold and nothing to send. So the crew's copy is drawn here, the
 * same page as the supplier's, and the two facts Lysa asked for are at the top
 * of it: whose house this is and where it is. A crew reading the order on a
 * driveway is reading it to find out exactly that.
 *
 * **The pay is optional and the default is to include it.** A work order's
 * money is what the crew is owed, not what the job is worth — the argument for
 * hiding it from a supplier does not apply to the people being paid it. But a
 * change order sent for a quote, or a copy going to somebody who is not the
 * crew, should be able to leave it out, so the caller says.
 */

import {
  COMPANY_NAME,
  formatCents,
  multiplyCents,
  type Cents,
} from "@roof-hawk/core";

import {
  GREY,
  heading,
  INK,
  logoDataUrl,
  MARGIN,
  money,
  PAGE,
  pair,
  RIGHT,
  rule,
  wide,
  type Doc,
} from "./orderPdfKit";

/** Right edges for the numbers, left edge for the description. */
const COLUMN = {
  item: MARGIN,
  itemWidth: 260,
  qty: 390,
  unitPrice: 480,
  total: RIGHT,
};

const ROWS_TOP = 110;
const ROWS_BOTTOM = PAGE.height - 150;

/** One line of work, as the crew reads it. */
export interface WorkOrderPdfLine {
  description: string;
  qty: number;
  unit: string;
  /** Already cents. The database column is dollars; the caller converts. */
  unitPrice: Cents;
}

export interface WorkOrderPdf {
  /** "WO-77". */
  number: string;
  crewName: string | null;
  /** Who it is for and where it is. The two facts the crew is looking for. */
  jobNumber: number | null;
  jobAddress: string;
  customerName: string | null;
  /** "Tue, Sep 15, 8:00 AM", or null when nobody has booked the day yet. */
  when: string | null;
  trades: string | null;
  description: string | null;
  lines: readonly WorkOrderPdfLine[];
  total: Cents;
  /** False leaves every price off the page. See the note above. */
  withPricing: boolean;
}

export const lineTotal = (line: WorkOrderPdfLine): Cents =>
  multiplyCents(line.unitPrice, Number(line.qty ?? 0));

function itemsHeader(doc: Doc, y: number, withPricing: boolean): number {
  heading(doc, "Work", COLUMN.item, y);
  doc.setTextColor(GREY);
  doc.setFontSize(10);
  doc.text("Quantity", COLUMN.qty, y, { align: "right" });
  if (withPricing) {
    doc.text("Rate", COLUMN.unitPrice, y, { align: "right" });
    doc.text("Total", COLUMN.total, y, { align: "right" });
  }
  doc.setTextColor(INK);
  rule(doc, y + 10);
  return y + 30;
}

function itemRow(
  doc: Doc,
  line: WorkOrderPdfLine,
  y: number,
  withPricing: boolean,
): number {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(INK);

  const wrapped = doc.splitTextToSize(
    line.description || "—",
    withPricing ? COLUMN.itemWidth : RIGHT - MARGIN - 120,
  );
  doc.text(wrapped, COLUMN.item, y);
  doc.text(`${line.qty ?? 0} ${line.unit ?? ""}`.trim(), COLUMN.qty, y, {
    align: "right",
  });
  if (withPricing) {
    doc.text(formatCents(line.unitPrice), COLUMN.unitPrice, y, {
      align: "right",
    });
    doc.text(formatCents(lineTotal(line)), COLUMN.total, y, { align: "right" });
  }

  const height = Math.max(wrapped.length, 1) * 14 + 10;
  rule(doc, y + height - 10);
  return y + height;
}

/** Draw the whole thing. Exported for the test, which reads the words back. */
export async function renderWorkOrder(
  order: WorkOrderPdf,
  options: { logo?: string | null } = {},
): Promise<Doc> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({
    unit: "pt",
    format: "letter",
    // Uncompressed, so a test can read the words back out of the file.
    compress: false,
  });

  doc.setProperties({
    title: `Work order ${order.number}`,
    author: COMPANY_NAME,
    subject: `Work order ${order.number}`,
  });

  // ------------------------------------------------------------- the head ---

  doc.setFont("helvetica", "normal");
  doc.setFontSize(26);
  doc.setTextColor(INK);
  doc.text("Work Order", MARGIN, 86);
  doc.setFontSize(14);
  doc.setTextColor(GREY);
  doc.text(order.number, MARGIN, 106);
  doc.setTextColor(INK);

  const logo = options.logo === undefined ? await logoDataUrl() : options.logo;
  if (logo) {
    try {
      doc.addImage(logo, "PNG", RIGHT - 60, 48, 60, 60);
    } catch {
      // A mark that will not draw is not a reason to withhold a work order.
    }
  }

  rule(doc, 124);

  // The homeowner first, because that is the question the crew is asking.
  pair(doc, "Homeowner", order.customerName ?? "", MARGIN, 150);
  pair(doc, "Job", order.jobNumber ? `#${order.jobNumber}` : "", 272, 150);
  pair(doc, "Crew", order.crewName ?? "", 400, 150);

  wide(doc, "Address", order.jobAddress, MARGIN, 196);

  pair(doc, "Day", order.when ?? "To be confirmed", MARGIN, 240);
  pair(doc, "Work", order.trades ?? "", 272, 240);

  let y = 300;
  if (order.description) {
    doc.setFontSize(10.5);
    doc.setTextColor(INK);
    doc.text(doc.splitTextToSize(order.description, RIGHT - MARGIN), MARGIN, y);
    y += 24;
  }

  // ------------------------------------------------------------ the items ---

  y = itemsHeader(doc, y + 10, order.withPricing);
  for (const line of order.lines) {
    if (y > ROWS_BOTTOM) {
      doc.addPage();
      y = itemsHeader(doc, ROWS_TOP, order.withPricing);
    }
    y = itemRow(doc, line, y, order.withPricing);
  }
  if (order.lines.length === 0) {
    doc.setFontSize(10.5);
    doc.setTextColor(GREY);
    doc.text("No line items.", COLUMN.item, y);
    doc.setTextColor(INK);
    y += 20;
  }

  // ----------------------------------------------------------- the totals ---

  if (order.withPricing) {
    if (y > ROWS_BOTTOM - 40) {
      doc.addPage();
      y = ROWS_TOP;
    }
    money(
      doc,
      "Total",
      order.total,
      y + 30,
      { label: COLUMN.unitPrice, value: COLUMN.total },
      true,
    );
  }

  return doc;
}

/** The file as base64, which is what the Gmail Edge Function attaches. */
export async function workOrderPdfBase64(
  order: WorkOrderPdf,
  options: { logo?: string | null } = {},
): Promise<string> {
  const doc = await renderWorkOrder(order, options);
  return doc.output("datauristring").split(",")[1] ?? "";
}

/** What the file is called in the crew's inbox. */
export const workOrderFilename = (number: string): string =>
  `${number.toLowerCase()}.pdf`;
